import * as THREE from 'three';
import { hexDist, hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { AscuasSky } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { clamp01, lum, repaint } from './halls.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The five strikes (Las vías 3, docs/SAGAS.md): the clearing re-dressed as the Deep Anvil's hall at the very bottom. The
 * node in the middle is the Anvil itself: a house-sized block of black iron on a stepped dwarven plinth, its face and
 * runes barely warm. The titan's vein runs to it as a dim crack across the floor. Cold flagstones, cold lamps, dark rock.
 *
 * The beat: a ring of light closes over the floor onto a fixed ring round the plinth; the instant they meet (the strike's
 * window) the target ring flashes white-hot. Nothing moves while the Anvil rests, only its embers breathing. Each strike
 * on the beat warms the hall a little (the Anvil's face, the vein, the sky lights); the fifth flushes it red.
 *
 * Draw calls: the hall (one merged mesh), the Anvil's hot parts and the vein (one mesh, its colour set each frame), the
 * lamps' glass, the two rings, a glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['anvil']>;

const STONE = ['#3c414c', '#434855', '#383d47', '#474c59'];
const RUNNER = ['#33312f', '#3a3734', '#2e2c2a'];
const GROUT = '#1d1f25';
const PILLAR = '#646a78';
const PILLAR_DARK = '#3c404a';
const RUNE = '#9fb0cc';
const IRON = '#33353c';
const IRON_EDGE = '#5a5e68';
const LAMP = new THREE.Color('#b8ccff');
const LAMP_CORE = new THREE.Color('#e8f0ff');
/** The vein and the Anvil's face: cold (an ember's dull red) to white-hot. */
const COLD = new THREE.Color('#5a1c0c');
const WARM = new THREE.Color('#ff6a2a');
const HOT = new THREE.Color('#ffe2b8');
const RING = new THREE.Color('#ff8a6e');
const RED = new THREE.Color('#ff4f3a');
/** The plinth's half-width, the target ring and where the closing ring starts (world). */
const PLINTH = 0.72;
const TARGET = 1.0;
const FAR = 4.4;

export class AnvilLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(96);
  private hotMat: THREE.MeshBasicMaterial;
  private target: THREE.Mesh;
  private closing: THREE.Mesh;
  private targetMat: THREE.MeshBasicMaterial;
  private closingMat: THREE.MeshBasicMaterial;
  private lamps: THREE.Vector3[] = [];
  /** Points along the vein (for its embers and glows). */
  private vein: THREE.Vector3[] = [];
  private top = new THREE.Vector3();
  private sky: AscuasSky | null = null;
  private skyCold: { hemi: THREE.Color; ground: THREE.Color; rim: THREE.Color; rimI: number; key: THREE.Color } | null = null;
  /** How warm the hall is drawn (eases toward strikes / need), and the fifth's red flush (fades). */
  private heat = 0;
  private flush = 0;
  /** A strike just landed: the target ring's burst (fades). */
  private struck = 0;
  private now = 0;
  private last = 0;
  private v = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
  ) {
    this.group.name = 'anvil-hall';
    const kit = new Kit();
    const hot = new Kit();
    const glass = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const slab = new THREE.CylinderGeometry(0.62, 0.64, 0.05, 6);
    const pillar = new THREE.CylinderGeometry(0.13, 0.15, 1, 8);
    const rock = new THREE.DodecahedronGeometry(0.14, 0);
    const R = map.bastion;
    const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
    const g0 = ctx.ground(0, 0);

    // The floor: a cold flagstone on every hex of the clearing but the middle one (the plinth stands there).
    let n = 0;
    for (let q = -R; q <= R; q++)
      for (let rr = -R; rr <= R; rr++) {
        const h = { q, r: rr };
        const d = hexDist(h, { q: 0, r: 0 });
        if (d > R || d === 0) continue;
        const c = hexToWorld(q, rr);
        const p = ctx.at(c.x, c.y, 0);
        const road = onPath.has(`${q},${rr}`);
        n++;
        kit.add(slab, (road ? RUNNER[n % RUNNER.length] : STONE[(n * 7 + q * 3) % STONE.length]) as string, place(p.x, ctx.ground(p.x, p.z) + 0.005, p.z, ((n * 13) % 5 - 2) * 0.008));
        if (!road && n % 3 === 0) kit.add(box, GROUT, place(p.x, ctx.ground(p.x, p.z) + 0.032, p.z, (n % 6) * 0.52, new THREE.Vector3(0.012, 0.006, 0.9)));
      }

    // The plinth: three stepped hexes of dressed stone, runes cut round the top step.
    for (const [i, [rad, hgt]] of ([[PLINTH + 0.1, 0.08], [PLINTH, 0.16], [PLINTH - 0.12, 0.24]] as const).entries()) {
      kit.add(new THREE.CylinderGeometry(rad, rad + 0.03, 0.08, 6), i === 1 ? PILLAR : PILLAR_DARK, place(0, g0 + hgt - 0.04, 0, Math.PI / 6));
    }
    for (let k = 0; k < 6; k++) {
      const a = (k / 6) * Math.PI * 2;
      kit.add(box, RUNE, place(Math.cos(a) * (PLINTH - 0.02), g0 + 0.16, Math.sin(a) * (PLINTH - 0.02), -a, new THREE.Vector3(0.012, 0.05, 0.1)));
    }
    // The Anvil: splayed feet, a narrow waist, a broad face with a long horn at one end and a square heel at the other;
    // black iron, its edges worn lighter. Bigger than a hero is tall.
    const base = g0 + 0.24;
    // Side-on to the camera, so the horn reads as a horn.
    const am = place(0, base, 0, 0, 1.35);
    for (const sx of [-1, 1]) {
      kit.add(box, IRON, am, place(sx * 0.3, 0.07, 0, sx * 0.0, new THREE.Vector3(0.36, 0.14, 0.62)));
      kit.add(box, IRON_EDGE, am, place(sx * 0.3, 0.145, 0, 0, new THREE.Vector3(0.3, 0.02, 0.56)));
    }
    kit.add(new THREE.CylinderGeometry(0.2, 0.3, 0.3, 4).rotateY(Math.PI / 4).scale(1.4, 1, 0.9), IRON, am, place(0, 0.3, 0));
    kit.add(box, IRON, am, place(0.05, 0.53, 0, 0, new THREE.Vector3(0.9, 0.18, 0.4)));
    kit.add(box, IRON_EDGE, am, place(0.05, 0.625, 0, 0, new THREE.Vector3(0.88, 0.02, 0.38)));
    // The horn: a long tapered cone off the face's end, a little lower than it.
    kit.add(new THREE.ConeGeometry(0.13, 0.46, 8).rotateZ(Math.PI / 2), IRON, am, place(-0.62, 0.56, 0));
    kit.add(box, IRON, am, place(-0.42, 0.53, 0, 0, new THREE.Vector3(0.12, 0.16, 0.3)));
    // The heel, squared, with its hardy hole.
    kit.add(box, IRON, am, place(0.58, 0.55, 0, 0, new THREE.Vector3(0.16, 0.14, 0.34)));
    // Its hot parts: the striking face, runes down each side of the waist, the hardy hole, a seam where the vein meets it.
    hot.add(box, '#ffffff', am, place(0.05, 0.638, 0, 0, new THREE.Vector3(0.78, 0.008, 0.3)));
    for (const sz of [-1, 1]) for (let k = 0; k < 4; k++) hot.add(box, '#ffffff', am, place(-0.15 + k * 0.1, 0.3, sz * 0.205, 0, new THREE.Vector3(0.035, 0.1 + (k % 2) * 0.05, 0.006)));
    hot.add(box, '#ffffff', am, place(0.58, 0.623, 0, 0, new THREE.Vector3(0.07, 0.006, 0.07)));
    hot.add(box, '#ffffff', am, place(0, 0.003, 0, 0, new THREE.Vector3(0.8, 0.006, 0.66)));
    this.top.set(0, base + 0.64 * 1.35, 0);

    // The vein: a crack in the floor from the rim (away from the roads) to the plinth, jagged, glowing faintly.
    const gates = map.sectors.map((sec) => {
      const c = hexToWorld(sec.gate.q, sec.gate.r);
      return Math.atan2(c.y, c.x);
    });
    const off = (a: number): number => Math.min(...gates.map((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g)))));
    let best = 0;
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      if (off(a) > off(best)) best = a;
    }
    const ER = (R + 0.75) * WORLD_PER_HEX;
    const from = ER + 1.2;
    let prev = new THREE.Vector3(Math.cos(best) * from, 0, Math.sin(best) * from);
    const steps = 16;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const rad = from + (PLINTH + 0.08 - from) * t;
      const wob = i === steps ? 0 : Math.sin(i * 2.7) * 0.22 + Math.sin(i * 5.1) * 0.08;
      const p = new THREE.Vector3(Math.cos(best + wob / Math.max(1, rad)) * rad, 0, Math.sin(best + wob / Math.max(1, rad)) * rad);
      const mid = prev.clone().add(p).multiplyScalar(0.5);
      const len = prev.distanceTo(p);
      const a = Math.atan2(p.x - prev.x, p.z - prev.z);
      const y = ctx.ground(mid.x, mid.z) + 0.04;
      // The crack: dark lips, and the glowing seam inside (wider near the Anvil).
      kit.add(box, '#14151a', place(mid.x, y - 0.005, mid.z, a, new THREE.Vector3(0.16 + t * 0.1, 0.02, len + 0.04)));
      hot.add(box, '#ffffff', place(mid.x, y + 0.008, mid.z, a, new THREE.Vector3(0.05 + t * 0.06, 0.012, len + 0.02)));
      if (i % 3 === 0) {
        // A side fissure now and then.
        const s = i % 2 ? 1 : -1;
        hot.add(box, '#ffffff', place(mid.x, y + 0.006, mid.z, a + s * 0.9, new THREE.Vector3(0.025, 0.01, 0.35)));
      }
      this.vein.push(mid.clone().setY(y));
      prev = p;
    }

    // Round the edge: carved pillars, clear of the roads' mouths and the vein, cold lamps on posts between them.
    const near = (a: number, list: number[], w: number): boolean => list.some((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) < w);
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2 + 0.1;
      if (near(a, gates, 0.32) || near(a, [best], 0.25)) continue;
      const p = new THREE.Vector3(Math.cos(a) * ER, 0, Math.sin(a) * ER);
      p.y = ctx.ground(p.x, p.z);
      if (i % 3 !== 1) this.pillar(kit, box, pillar, p, i);
      else this.lampPost(kit, glass, box, p, a + Math.PI, i);
    }
    // Cold dark rock beyond the edge, heaped behind the pillars.
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2;
      if (near(a, gates, 0.28) || near(a, [best], 0.12)) continue;
      const rr = ER + 0.7 + (i % 3) * 0.35;
      const p = new THREE.Vector3(Math.cos(a) * rr, 0, Math.sin(a) * rr);
      kit.add(rock, ['#1f2127', '#282a31', '#1b1c21'][i % 3] as string, place(p.x, ctx.ground(p.x, p.z) + 0.08, p.z, i * 1.3, 1.6 + (i % 4) * 0.5, i * 0.7));
    }

    const hallMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.8, metalness: 0.12, flatShading: true });
    const hall = kit.build(hallMat, 'anvil-hall');
    this.mats.push(hallMat);
    if (hall) {
      hall.receiveShadow = true;
      hall.castShadow = !ctx.low;
      this.geos.push(hall.geometry);
      this.group.add(hall);
    }
    this.hotMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: COLD.clone() });
    const hotMesh = hot.build(this.hotMat, 'anvil-hot');
    this.mats.push(this.hotMat);
    if (hotMesh) {
      this.geos.push(hotMesh.geometry);
      this.group.add(hotMesh);
    }
    const glassMat = new THREE.MeshBasicMaterial({ vertexColors: true });
    const lit = glass.build(glassMat, 'anvil-lamps');
    this.mats.push(glassMat);
    if (lit) {
      this.geos.push(lit.geometry);
      this.group.add(lit);
    }

    // The beat: a fixed ring round the plinth, and the ring that closes onto it (scaled each frame).
    const ringGeo = (w: number): THREE.BufferGeometry => new THREE.RingGeometry(1 - w, 1, 64).rotateX(-Math.PI / 2);
    const tg = ringGeo(0.09);
    const cg = ringGeo(0.06);
    this.geos.push(tg, cg);
    const ringMat = (): THREE.MeshBasicMaterial => new THREE.MeshBasicMaterial({ color: RING.clone(), transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.targetMat = ringMat();
    this.closingMat = ringMat();
    this.mats.push(this.targetMat, this.closingMat);
    this.target = new THREE.Mesh(tg, this.targetMat);
    this.closing = new THREE.Mesh(cg, this.closingMat);
    for (const m of [this.target, this.closing]) {
      m.position.set(0, g0 + 0.06, 0);
      m.renderOrder = 2;
      m.frustumCulled = false;
    }
    this.target.scale.setScalar(TARGET);
    this.closing.visible = false;
    this.group.add(this.target, this.closing, this.glow.mesh);
    for (const gg of [box, slab, pillar, rock]) gg.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** A carved pillar: a square foot, a round shaft cut with a rune band, a capital. */
  private pillar(kit: Kit, box: THREE.BoxGeometry, shaft: THREE.CylinderGeometry, p: THREE.Vector3, i: number): void {
    const m = place(p.x, p.y, p.z, i * 0.7);
    kit.add(box, PILLAR_DARK, m, place(0, 0.1, 0, 0, new THREE.Vector3(0.42, 0.2, 0.42)));
    kit.add(shaft, PILLAR, m, place(0, 1.05, 0, 0, new THREE.Vector3(1.1, 1.7, 1.1)));
    kit.add(new THREE.TorusGeometry(0.17, 0.025, 4, 10).rotateX(Math.PI / 2), RUNE, m, place(0, 0.8, 0));
    kit.add(box, PILLAR_DARK, m, place(0, 1.96, 0, 0, new THREE.Vector3(0.46, 0.14, 0.46)));
  }

  /** A cold lamp on a post: an iron post, an arm, a cage with pale glass (its glow is drawn each frame). */
  private lampPost(kit: Kit, glass: Kit, box: THREE.BoxGeometry, p: THREE.Vector3, yaw: number, i: number): void {
    const m = place(p.x, p.y, p.z, yaw);
    kit.add(box, PILLAR_DARK, m, place(0, 0.05, 0, 0, new THREE.Vector3(0.24, 0.1, 0.24)));
    kit.add(box, IRON_EDGE, m, place(0, 0.62, 0, 0, new THREE.Vector3(0.07, 1.24, 0.07)));
    kit.add(box, IRON_EDGE, m, place(0, 1.2, 0.14, 0, new THREE.Vector3(0.05, 0.05, 0.34)));
    const mm = m.clone().multiply(place(0, 1.02, 0.28));
    kit.add(box, IRON, mm, place(0, 0.13, 0, 0, new THREE.Vector3(0.16, 0.03, 0.16)));
    kit.add(box, IRON, mm, place(0, -0.09, 0, 0, new THREE.Vector3(0.14, 0.03, 0.14)));
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) kit.add(box, IRON, mm, place(x * 0.065, 0.02, z * 0.065, 0, new THREE.Vector3(0.015, 0.2, 0.015)));
    glass.add(new THREE.CylinderGeometry(0.05, 0.055, 0.15, 6), LAMP, mm, place(0, 0.02, 0));
    this.lamps.push(new THREE.Vector3(0, 0.02, 0).applyMatrix4(mm));
    void i;
  }

  /** Re-dresses the board as the hall: cold stone, the woods to dark spires, black overhead, cold light. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: AscuasSky | null, extra: THREE.Object3D[]): void {
    repaint(terrain.mesh.geometry, new THREE.Color('#15171c'), new THREE.Color('#3c414c'), 0.03, 0.22);
    const c = new THREE.Color();
    const spire = [new THREE.Color('#202228'), new THREE.Color('#33363e'), new THREE.Color('#2a2c33')];
    const rock = (o: THREE.Object3D): void => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        repaint(mesh.geometry, new THREE.Color('#08090b'), new THREE.Color('#24262d'), 0.008, 0.11);
        return;
      }
      if (o.name === 'roads') {
        repaint(mesh.geometry, new THREE.Color('#18171a'), new THREE.Color('#3a3735'), 0.02, 0.14);
        return;
      }
      if (o.name === 'tufts') {
        o.visible = false;
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      if (mat.emissive && mat.emissive.getHex() !== 0) return;
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.instanceColor) {
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = clamp01(lum(c.r, c.g, c.b) / 0.12);
          c.copy(spire[i % 3] as THREE.Color).lerp(spire[1] as THREE.Color, l * 0.5);
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
        return;
      }
      if (mat.vertexColors || mat.map) return;
      const m2 = mat.clone();
      m2.color.lerp(spire[0] as THREE.Color, 0.7);
      mesh.material = m2;
      this.mats.push(m2);
    };
    terrain.decor.traverse(rock);
    for (const o of extra) o.traverse(rock);
    scene.background = new THREE.Color('#06070a');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#0a0b0f');
    terrain.fog?.tint({ low: '#121620', mid: '#0d0f14', far: '#06070a', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#8e9ab8');
      sky.hemi.groundColor.set('#1a1c24');
      sky.hemi.intensity *= 0.78;
      sky.key.color.set('#d6e0f0');
      sky.key.intensity *= 0.7;
      sky.rim.color.set('#6a7894');
      sky.rim.intensity *= 0.5;
      this.sky = sky;
      this.skyCold = { hemi: sky.hemi.color.clone(), ground: sky.hemi.groundColor.clone(), rim: sky.rim.color.clone(), rimI: sky.rim.intensity, key: sky.key.color.clone() };
    }
  }

  /** Every fresh tick: the strikes (a burst and a heat wave, a dull clank, the vein catching), embers off the vein. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    const st = s.anvil;
    // Embers off the vein, more of them the warmer it is.
    const every = Math.max(2, Math.round((low ? 12 : 7) - this.heat * 4));
    if (s.tick % every === 0 && this.vein.length) {
      const p = this.vein[(s.tick * 7) % this.vein.length] as THREE.Vector3;
      this.ctx.vfx.burst(p, { n: 1, color: this.heat > 0.3 ? '#ff8a4a' : '#7a2a14', speed: 0.006, up: 0.03, size: 0.05, life: 30, gravity: -0.0015, spread: 0.2 });
    }
    // Resting: the Anvil breathes a faint ember now and then.
    if (st && !st.done && s.waveT < st.open && s.tick % 18 === 0) this.ctx.vfx.burst(this.top, { n: low ? 1 : 3, color: '#ff6a2a', speed: 0.01, up: 0.04, size: 0.05, life: 26, gravity: -0.002, spread: 0.35 });
    const rad = (this.r.radius / 1000) * WORLD_PER_HEX;
    for (const e of s._events ?? []) {
      if (e.t !== 'anvil') continue;
      if (e.kind === 'hit') {
        // The hammer on the face: a burst of sparks, a white flare, and the heat rolling out over the hall.
        this.struck = 1;
        this.ctx.flare(this.top, '#fff0d8', 3.4, 12);
        this.ctx.vfx.burst(this.top, { n: low ? 14 : 34, color: '#ffc27a', speed: 0.09, up: 0.07, size: 0.045, life: 16, gravity: 0.01, spread: 0.25 });
        const g = this.v.set(0, this.ctx.ground(0, 0) + 0.08, 0);
        this.ctx.vfx.ring(g, TARGET, rad, '#ff8a4a', 16, 0.85);
        this.ctx.vfx.ring(g, TARGET * 0.8, rad * 0.75, '#ffe2b8', 12, 0.6);
        if (!low) this.ctx.vfx.ring(g, TARGET, rad * 0.55, '#ff4f3a', 20, 0.4);
        this.ctx.shake(0.16);
        // The heat runs up the vein too.
        for (let i = 0; i < this.vein.length; i += low ? 4 : 2) this.ctx.vfx.burst(this.vein[i] as THREE.Vector3, { n: 2, color: '#ff8a4a', speed: 0.02, up: 0.05, size: 0.05, life: 18, gravity: -0.002, spread: 0.2 });
      } else if (e.kind === 'miss') {
        // Off the beat: a dull clank, a few grey sparks where the hero stood.
        const p = this.ctx.at(e.x, e.y, 0.6);
        this.ctx.vfx.burst(p, { n: low ? 4 : 9, color: '#8a8e96', speed: 0.04, up: 0.03, size: 0.03, life: 10, gravity: 0.012, spread: 0.2 });
        this.ctx.vfx.burst(this.top, { n: 3, color: '#55545c', speed: 0.01, up: 0.02, size: 0.22, life: 16, smoke: true, spread: 0.3 });
      } else if (e.kind === 'fifth') {
        // The vein catches: the hall flushes red, a great wave, embers and ash rising everywhere.
        this.struck = 1;
        this.flush = 1;
        this.ctx.flare(this.top, '#ffb070', 2.8, 22);
        this.ctx.vfx.burst(this.top, { n: low ? 20 : 44, color: '#ffb070', speed: 0.12, up: 0.1, size: 0.045, life: 22, gravity: 0.008, spread: 0.3 });
        const g = this.v.set(0, this.ctx.ground(0, 0) + 0.08, 0);
        for (const [r0, r1, col, life] of [[TARGET, rad * 1.3, '#ff4f3a', 26], [TARGET, rad, '#ff8a4a', 20], [TARGET * 0.6, rad * 0.6, '#ffd6b0', 14]] as const) this.ctx.vfx.ring(g, r0, r1, col, life, 0.55);
        this.ctx.shake(0.25);
        for (let i = 0; i < this.vein.length; i++) this.ctx.vfx.burst(this.vein[i] as THREE.Vector3, { n: low ? 2 : 5, color: '#ff8a4a', speed: 0.03, up: 0.08, size: 0.06, life: 30, gravity: -0.003, spread: 0.3 });
        // What was left in the hall goes to ash where it stood.
        let n = 0;
        for (const o of s.enemies) {
          if (n >= (low ? 12 : 28)) break;
          const p = this.ctx.pos(o.id);
          if (!p) continue;
          n++;
          this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#5a5650', speed: 0.02, up: 0.05, size: 0.25, life: 34, smoke: true, spread: 0.35 });
          this.ctx.vfx.burst(p, { n: low ? 3 : 6, color: '#ff6a2a', speed: 0.02, up: 0.07, size: 0.05, life: 28, gravity: -0.003, spread: 0.3 });
        }
      }
    }
  }

  /** Every redraw: the hall's warmth, the beat's two rings, the glows. `alpha`: how far into the tick; `hurt`: the Anvil was just struck by a nightmare. */
  draw(s: BattleState, alpha: number, hurt: boolean): void {
    const r = this.r;
    const st = s.anvil;
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    const want = st ? Math.min(1, st.strikes / r.need) : 0;
    this.heat += (want - this.heat) * Math.min(1, dt * 1.5);
    this.flush = Math.max(st?.done ? 0.35 : 0, this.flush - dt * 0.4);
    this.struck = Math.max(0, this.struck - dt * 2.5);
    const heat = Math.max(this.heat, this.flush);

    // The hot parts: an ember's red when cold, warmer with each strike, white-hot in a strike's flash.
    this.c.copy(COLD).lerp(WARM, clamp01(heat * 1.1)).lerp(HOT, clamp01(this.struck * 0.8 + this.flush * 0.6));
    const breathe = st && !st.done && s.waveT < st.open ? 0.85 + 0.15 * Math.sin(this.now * 2.2) : 1;
    this.hotMat.color.copy(this.c).multiplyScalar(breathe);

    // The sky lights: from the hall's cold toward the vein's red.
    if (this.sky && this.skyCold) {
      const k = clamp01(heat * 0.55 + this.flush * 0.5);
      this.sky.hemi.color.copy(this.skyCold.hemi).lerp(this.c.set('#c09080'), k * 0.6);
      this.sky.hemi.groundColor.copy(this.skyCold.ground).lerp(this.c.set('#4a160c'), k);
      this.sky.rim.color.copy(this.skyCold.rim).lerp(RED, k);
      this.sky.rim.intensity = this.skyCold.rimI * (1 + k * 1.6);
      this.sky.key.color.copy(this.skyCold.key).lerp(this.c.set('#ffd6c0'), k * 0.5);
    }

    // The beat: nothing closing while it rests; then the ring closes onto the target; in the window, the target flashes.
    const running = !!st && s.started && !st.done && s.result === 'running';
    const t = s.waveT + alpha;
    let flash = 0;
    if (running && st && t >= st.open) {
      const k = clamp01((t - st.open) / Math.max(1, st.close - st.open));
      // At an even pace, so the close can be read ahead.
      const rr = FAR + (TARGET - FAR) * k;
      this.closing.visible = true;
      this.closing.scale.setScalar(Math.max(TARGET, rr));
      this.closingMat.opacity = 0.25 + 0.6 * k;
      this.closingMat.color.copy(RING).lerp(HOT, k * k);
      flash = Math.abs(t - st.close) <= r.window ? 1 - Math.abs(t - st.close) / (r.window + 1) : 0;
      if (t > st.close + r.window) this.closing.visible = false;
    } else this.closing.visible = false;
    const pulse = running ? 0.35 + 0.1 * Math.sin(this.now * 3) : 0.12;
    this.targetMat.opacity = Math.min(1, pulse + flash * 0.65 + this.struck * 0.5);
    this.targetMat.color.copy(RING).lerp(HOT, Math.max(flash, this.struck));
    this.target.scale.setScalar(TARGET * (1 + flash * 0.06 + this.struck * 0.08));
    this.target.visible = running || this.struck > 0.01;

    this.glow.begin();
    for (let i = 0; i < this.lamps.length; i++) {
      const p = this.lamps[i] as THREE.Vector3;
      const flick = 0.9 + 0.1 * Math.sin(this.now * 5 + i * 2.3);
      this.glow.add(p.x, p.y, p.z, LAMP_CORE, 0.3, 0.6 * flick);
      this.glow.add(p.x, p.y, p.z, LAMP, 1.3, 0.18 * flick);
      this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, LAMP, 2, 0.05 * flick);
    }
    // The vein's glow, and the Anvil's: an ember at rest, warmer with each strike, red in the flush.
    for (let i = 0; i < this.vein.length; i += 3) {
      const p = this.vein[i] as THREE.Vector3;
      this.glow.add(p.x, p.y + 0.05, p.z, this.c.copy(COLD).lerp(WARM, heat), 0.9 + heat, 0.06 + 0.16 * heat);
    }
    this.glow.add(this.top.x, this.top.y, this.top.z, this.c.copy(WARM), 1.6 + heat * 2, (0.08 + 0.25 * heat) * breathe + this.struck * 0.5);
    this.glow.add(0, this.ctx.ground(0, 0) + 0.1, 0, RED, 3.5 + heat * 4 + this.flush * 4, 0.05 + 0.1 * heat + this.flush * 0.12);
    if (flash > 0) this.glow.add(0, this.ctx.ground(0, 0) + 0.12, 0, HOT, TARGET * 2.6, 0.35 * flash);
    if (hurt) this.glow.add(this.top.x, this.top.y - 0.3, this.top.z, RED, 1.4, 0.35);
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
